import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { SortingOption } from '@/components/shared/tattoo/filters/SortingFilter'
import type { ArtistSortingOption } from '@/components/shared/artist/ArtistSortingFilter'

export type DiscoverType = 'flashes' | 'tattoos' | 'artists'

export interface DiscoverUrlFilterState {
  discoverType: DiscoverType
  selectedLocations: string[]
  tattooSorting: SortingOption
  artistSorting: ArtistSortingOption
  includeBooked: boolean
  showDeals: boolean
  showFollowingOnly: boolean
  activeTab: 'map' | 'list'
}

function normalizeDiscoverType(raw: string | null, legacyContentType: string | null): DiscoverType {
  if (raw === 'flashes' || raw === 'tattoos' || raw === 'artists') {
    return raw
  }
  if (legacyContentType === 'tattoos') return 'tattoos'
  if (legacyContentType === 'flashes') return 'flashes'
  return 'flashes'
}

function normalizeTattooSortingFromUrl(raw: string | null): SortingOption {
  if (raw === 'event') return 'recent'
  const allowed: SortingOption[] = ['trending', 'recent', 'fyp', 'priceAsc', 'priceDesc']
  if (raw && allowed.includes(raw as SortingOption)) {
    return raw as SortingOption
  }
  return 'recent'
}

function normalizeArtistSortingFromUrl(raw: string | null): ArtistSortingOption {
  const allowed: ArtistSortingOption[] = ['discover', 'trending', 'newest']
  if (raw && allowed.includes(raw as ArtistSortingOption)) {
    return raw as ArtistSortingOption
  }
  return 'discover'
}

export function useDiscoverUrlFilters() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const allLocationsText = t('onskin.filters.allLocations')

  const filters = useMemo<DiscoverUrlFilterState>(() => {
    const typeParam = searchParams.get('type')
    const legacyContentType = searchParams.get('content-type')
    const locationParam = searchParams.get('location')
    const cityLegacy = searchParams.get('city')
    const sortingParam = searchParams.get('sorting')
    const includeBookedParam = searchParams.get('includeBooked')
    const dealsParam = searchParams.get('deals')
    const followingParam = searchParams.get('following')
    const tabParam = searchParams.get('tab') as 'map' | 'list' | null

    let selectedLocations: string[]
    if (locationParam) {
      selectedLocations = locationParam.split(',').filter(Boolean)
    } else if (cityLegacy) {
      selectedLocations = [cityLegacy]
    } else {
      selectedLocations = []
    }

    const discoverType = normalizeDiscoverType(typeParam, legacyContentType)

    return {
      discoverType,
      selectedLocations,
      tattooSorting: normalizeTattooSortingFromUrl(sortingParam),
      artistSorting: normalizeArtistSortingFromUrl(sortingParam),
      includeBooked: includeBookedParam === 'true',
      showDeals: dealsParam === 'true',
      showFollowingOnly: followingParam === 'true',
      activeTab: tabParam || 'list',
    }
  }, [searchParams])

  const updateUrlParams = useCallback(
    (newFilters: Partial<DiscoverUrlFilterState>) => {
      const updatedFilters = { ...filters, ...newFilters }
      const newSearchParams = new URLSearchParams(searchParams)

      if (updatedFilters.discoverType === 'flashes') {
        newSearchParams.delete('type')
      } else {
        newSearchParams.set('type', updatedFilters.discoverType)
      }
      newSearchParams.delete('content-type')

      const loc = updatedFilters.selectedLocations
      if (!loc?.length || (loc.length === 1 && loc[0] === allLocationsText)) {
        newSearchParams.delete('location')
      } else if (loc) {
        newSearchParams.set('location', loc.join(','))
      }
      newSearchParams.delete('city')

      const sorting =
        updatedFilters.discoverType === 'artists'
          ? updatedFilters.artistSorting
          : updatedFilters.tattooSorting

      const sortingIsDefault =
        updatedFilters.discoverType === 'artists'
          ? sorting === 'discover'
          : sorting === 'recent'

      if (sortingIsDefault) {
        newSearchParams.delete('sorting')
      } else {
        newSearchParams.set('sorting', sorting)
      }

      if (updatedFilters.includeBooked) {
        newSearchParams.set('includeBooked', 'true')
      } else {
        newSearchParams.delete('includeBooked')
      }

      if (updatedFilters.showDeals) {
        newSearchParams.set('deals', 'true')
      } else {
        newSearchParams.delete('deals')
      }

      if (updatedFilters.discoverType === 'artists' && updatedFilters.showFollowingOnly) {
        newSearchParams.set('following', 'true')
      } else {
        newSearchParams.delete('following')
      }

      if (updatedFilters.discoverType === 'artists' && updatedFilters.activeTab === 'map') {
        newSearchParams.set('tab', 'map')
      } else {
        newSearchParams.delete('tab')
      }

      const newUrlString = newSearchParams.toString()
      const currentUrlString = searchParams.toString()

      if (newUrlString !== currentUrlString) {
        setSearchParams(newSearchParams, { replace: true })
      }
    },
    [filters, searchParams, setSearchParams, allLocationsText],
  )

  const setDiscoverType = useCallback(
    (discoverType: DiscoverType) => {
      updateUrlParams({ discoverType })
    },
    [updateUrlParams],
  )

  const setSelectedLocations = useCallback(
    (selectedLocations: string[]) => {
      updateUrlParams({ selectedLocations })
    },
    [updateUrlParams],
  )

  const setTattooSorting = useCallback(
    (tattooSorting: SortingOption) => {
      if (tattooSorting === 'fyp' && filters.showDeals) {
        updateUrlParams({ tattooSorting, showDeals: false })
        return
      }
      updateUrlParams({ tattooSorting })
    },
    [updateUrlParams, filters.showDeals],
  )

  const setArtistSorting = useCallback(
    (artistSorting: ArtistSortingOption) => {
      updateUrlParams({ artistSorting })
    },
    [updateUrlParams],
  )

  const setIncludeBooked = useCallback(
    (includeBooked: boolean) => {
      updateUrlParams({ includeBooked })
    },
    [updateUrlParams],
  )

  const setShowDeals = useCallback(
    (showDeals: boolean) => {
      if (showDeals) {
        updateUrlParams({ showDeals: true, tattooSorting: 'recent' })
      } else {
        updateUrlParams({ showDeals: false })
      }
    },
    [updateUrlParams],
  )

  const setShowFollowingOnly = useCallback(
    (showFollowingOnly: boolean) => {
      updateUrlParams({ showFollowingOnly })
    },
    [updateUrlParams],
  )

  const setActiveTab = useCallback(
    (activeTab: 'map' | 'list') => {
      updateUrlParams({ activeTab })
    },
    [updateUrlParams],
  )

  const applyDiscoverFilters = useCallback(
    (patch: Partial<DiscoverUrlFilterState>) => {
      updateUrlParams(patch)
    },
    [updateUrlParams],
  )

  return {
    ...filters,
    allLocationsText,
    setDiscoverType,
    setSelectedLocations,
    setTattooSorting,
    setArtistSorting,
    setIncludeBooked,
    setShowDeals,
    setShowFollowingOnly,
    setActiveTab,
    applyDiscoverFilters,
  }
}
